import React, {useMemo, useCallback, useState} from 'react';
import styles from './ProjectDocumentsTable.module.scss';
import {TableColumnConfig, Text, Spin, Button, DropdownMenu, Icon} from '@gravity-ui/uikit';
import {DataTable, formatDateToLocalDate} from '../../shared';
import {ProjectDocumentModel} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';
import {useNavigate} from 'react-router';
import {TrashBin} from '@gravity-ui/icons';
import {DeleteProjectDocumentModal} from '../../features';

interface ProjectDocumentsTableProps {
  documents: ProjectDocumentModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
}

export const ProjectDocumentsTable = ({
  documents,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: ProjectDocumentsTableProps) => {
  const SKELETON_ROWS = 5;
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);

  const handleRowClick = useCallback(
    (document: ProjectDocumentModel) => {
      navigate(`/project/documents/${document.id}`);
    },
    [navigate],
  );

  const handleDeleteDocument = useCallback((document: ProjectDocumentModel) => {
    setDeletingDocumentId(document.id);
    setIsDeleteModalOpen(true);
  }, []);

  const columns = useMemo<TableColumnConfig<ProjectDocumentModel>[]>(
    () => [
      {
        id: 'title',
        name: 'Название',
        template: (item: ProjectDocumentModel) => <Text variant="body-1">{item.title}</Text>,
      },
      {
        id: 'priority',
        name: 'Порядок показа',
        template: (item: ProjectDocumentModel) => <Text variant="body-1">{item.priority}</Text>,
      },
      {
        id: 'createdAt',
        name: 'Дата добавления',
        template: (item: ProjectDocumentModel) => <Text variant="body-1">{formatDateToLocalDate(item.createdAt)}</Text>,
      },
      {
        id: 'more',
        name: () => <div className={styles.tableHeader}></div>,
        template: (item: ProjectDocumentModel) => (
          <div className={styles.moreCell} onClick={(e) => e.stopPropagation()}>
            <DropdownMenu
              items={[
                {
                  iconStart: <Icon size={16} data={TrashBin} />,
                  action: () => handleDeleteDocument(item),
                  text: 'Удалить',
                  className: styles.dropdownItemDelete,
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [handleDeleteDocument],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<ProjectDocumentModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={documents || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Документы не найдены"
        emptyStateDescription="Документы с таким названием не найдены. Попробуйте изменить запрос"
        onRowClick={handleRowClick}
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}

      <DeleteProjectDocumentModal open={isDeleteModalOpen} setOpen={setIsDeleteModalOpen} documentId={deletingDocumentId} />
    </div>
  );
};

